/**
 * Onboarding data hooks — the client seam for the ZEUS contractor cascade.
 *
 * Unlike the dual-mode hooks in useMutations.ts, onboarding is inherently
 * backend-driven (there is no seed for live invite tokens), so these call the
 * API directly. peek is a query; the rest are useState-isPending mutations that
 * mirror the mutation-hook shape used elsewhere.
 *
 * After start() succeeds we persist the issued JWT via the auth store, so the
 * axios request interceptor carries it on every subsequent authed step call and
 * AuthGuard treats the onboarding user as a real session.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api, onboardingApi, demoModeActive } from "./api";
import type {
  OnboardingPeek,
  OnboardingStartInput,
  OnboardingStartResult,
  WizardSession,
  OnboardingInvite,
  CreateInviteInput,
  CreateInviteResult,
  StatementRowInput,
  StatementFileMeta,
  StatementImportResult,
  SearchableUser,
  AddMemberInput,
  AddMemberResult,
  LeadContractorCandidate,
  AssignLeadContractorInput,
  CreateZeusProjectResult,
  GateDetail,
} from "./api";
import { useAuthStore } from "./authStore";

function extractError(e: unknown): string {
  if (e instanceof AxiosError) {
    const data = e.response?.data as { error?: string; message?: string } | undefined;
    return data?.message || data?.error || e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

// Peek the invite context (public). Does not consume the link.
export function usePeekInvite(token: string | undefined) {
  return useQuery<OnboardingPeek>({
    queryKey: ["onboarding", "peek", token],
    queryFn: () => onboardingApi.peek(token as string),
    enabled: !!token,
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

// Redeem + begin the wizard, then persist the returned session token.
export function useStartOnboarding() {
  const login = useAuthStore((s) => s.login);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(
    token: string,
    input: OnboardingStartInput = {},
  ): Promise<OnboardingStartResult | null> {
    setIsPending(true);
    setError(null);
    try {
      const result = await onboardingApi.start(token, input);
      login(result.user, result.token);
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { start, isPending, error };
}

// Resume an in-progress session for a (possibly already-consumed) link.
export function useResumeOnboarding() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function resume(
    token: string,
  ): Promise<{ session: WizardSession; invite: OnboardingInvite } | null> {
    setIsPending(true);
    setError(null);
    try {
      return await onboardingApi.resume(token);
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { resume, isPending, error };
}

// Autosave draft data for a step without advancing the machine.
export function useSaveStep() {
  const [isPending, setIsPending] = useState(false);

  async function saveStep(
    sessionId: number,
    step: string,
    data: Record<string, unknown>,
  ): Promise<WizardSession | null> {
    setIsPending(true);
    try {
      const { session } = await onboardingApi.saveStep(sessionId, step, data);
      return session;
    } catch {
      // Autosave is best-effort; the local cache still holds the draft.
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { saveStep, isPending };
}

// Commit a step (runs its backend domain mutation) and advance the wizard.
export function useCompleteStep() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function completeStep(
    sessionId: number,
    step: string,
    data: Record<string, unknown> = {},
  ): Promise<WizardSession | null> {
    setIsPending(true);
    setError(null);
    try {
      const { session } = await onboardingApi.completeStep(sessionId, step, data);
      return session;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { completeStep, isPending, error };
}

// Import parsed bank-statement rows for the bank step (method "statement").
export function useUploadStatement() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function uploadStatement(
    sessionId: number,
    rows: StatementRowInput[],
    fileMeta: StatementFileMeta,
  ): Promise<StatementImportResult | null> {
    setIsPending(true);
    setError(null);
    try {
      const { result } = await onboardingApi.uploadStatement(sessionId, { rows, fileMeta });
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { uploadStatement, isPending, error };
}

// Set the invited user's account password (Account step).
export function useSetPassword() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function setPassword(sessionId: number, password: string): Promise<boolean> {
    setIsPending(true);
    setError(null);
    try {
      await onboardingApi.setPassword(sessionId, password);
      return true;
    } catch (e) {
      setError(extractError(e));
      return false;
    } finally {
      setIsPending(false);
    }
  }

  return { setPassword, isPending, error };
}

// Finalise onboarding; returns the landing route from the backend.
export function useCompleteOnboarding() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function completeOnboarding(
    sessionId: number,
  ): Promise<{ session: WizardSession; landing: string } | null> {
    setIsPending(true);
    setError(null);
    try {
      return await onboardingApi.completeSession(sessionId);
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { completeOnboarding, isPending, error };
}

// Create an invite (spec §9.5) — used by the in-product invite modal.
export function useCreateInvite() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function createInvite(input: CreateInviteInput): Promise<CreateInviteResult | null> {
    setIsPending(true);
    setError(null);
    try {
      return await onboardingApi.createInvite(input);
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { createInvite, isPending, error };
}

// "This is not me" — forward the invite to the correct recipient.
export function useForwardInvite() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function forward(
    token: string,
    input: { email: string; name?: string },
  ): Promise<boolean> {
    setIsPending(true);
    setError(null);
    try {
      await onboardingApi.forward(token, input);
      return true;
    } catch (e) {
      setError(extractError(e));
      return false;
    } finally {
      setIsPending(false);
    }
  }

  return { forward, isPending, error };
}

// "Add members" — live search of existing platform users for a project, so
// a known member can be added directly instead of going through Invite's
// email/onboarding-link flow. Enabled only once the query is >= 2 chars,
// matching the backend's own minimum-length guard (searchUsersByPrefix).
export function useSearchProjectUsers(projectId: number | string | undefined, query: string) {
  return useQuery<SearchableUser[]>({
    queryKey: ["project-users-search", projectId, query],
    queryFn: () => api.searchProjectUsers(projectId as number | string, query),
    enabled: !!projectId && query.trim().length >= 2,
    staleTime: 10_000,
    refetchOnWindowFocus: false,
  });
}

// Add an already-registered user straight to a project (spec: "Add members",
// distinct from Invite). No onboarding link is issued — see
// cascade.addExistingMemberDirect on the backend.
export function useAddProjectMember() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function addMember(
    projectId: number | string,
    input: AddMemberInput,
  ): Promise<AddMemberResult | null> {
    setIsPending(true);
    setError(null);
    try {
      return await api.addProjectMember(projectId, input);
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { addMember, isPending, error };
}

// "Assign lead contractor" — search active organisations of the right type
// (prime contractor for a top-level project, subcontractor for a
// sub-project) to attach directly, distinct from inviting a brand-new
// organisation through the full cascade/onboarding flow.
export function useSearchLeadContractorCandidates(projectId: number | string | undefined, query: string) {
  return useQuery<LeadContractorCandidate[]>({
    queryKey: ["lead-contractor-candidates", projectId, query],
    queryFn: () => api.getLeadContractorCandidates(projectId as number | string, query),
    enabled: !!projectId && query.trim().length >= 2,
    staleTime: 10_000,
    refetchOnWindowFocus: false,
  });
}

export function useAssignLeadContractor() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function assignLeadContractor(
    projectId: number | string,
    input: AssignLeadContractorInput,
  ): Promise<CreateZeusProjectResult | null> {
    setIsPending(true);
    setError(null);
    try {
      return await api.assignLeadContractor(projectId, input);
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { assignLeadContractor, isPending, error };
}

// "Remove member" - oversight (or a prime_lead/sub_lead removing their own
// appointee, per the server's role-pack check) revoking someone's project
// membership. No demo-mode branch, matching useAddProjectMember/
// useAssignLeadContractor above - there's no demo-seed equivalent for either
// of those, and TeamTab already disables this action when a member has no
// real membershipId (i.e. is a demo-only row).
export function useRemoveProjectMember() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function removeMember(membershipId: number, justification: string): Promise<boolean> {
    setIsPending(true);
    setError(null);
    try {
      await api.removeProjectMember(membershipId, justification);
      return true;
    } catch (e) {
      setError(extractError(e));
      return false;
    } finally {
      setIsPending(false);
    }
  }

  return { removeMember, isPending, error };
}

// The full onboarding submission behind one approval-gate row (spec §9.6).
// Demo mode has no real oversight session and its gates are seed orgs, so the
// drawer reads the seed store there and this stays disabled; off demo it is the
// only source of the document-level detail (documents, invites, contact, KYC
// queries) — none of which is on the queue row.
export function useGateDetail(organisationId: string | null) {
  const demo = demoModeActive();
  return useQuery<GateDetail>({
    queryKey: ["onboarding", "gate-detail", organisationId],
    queryFn: () => onboardingApi.getGate(organisationId as string),
    enabled: !demo && !!organisationId,
    retry: false,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}
